import React, { useState } from "react";
import facebookSvg from "images/Facebook.svg";
import twitterSvg from "images/Twitter.svg";
import googleSvg from "images/Google.svg";
import Input from "componentsTemplate/Input/Input";
import ButtonPrimary from "componentsTemplate/Button/ButtonPrimary";
import NcLink from "componentsTemplate/NcLink/NcLink";
import Heading2 from "componentsTemplate/Heading/Heading2";
import Image from "componentsTemplate/Image";
import Layout from "../layout";

import useAuth from "hooks/useAuth";
import { useForm, Controller } from "react-hook-form";
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup'; // Import this package for TypeScript support
import { showToastError, showToastSuccess } from "utils/toasts";

// Icons
import { AiOutlineEyeInvisible, AiOutlineEye } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import InputMasked from "componentsTemplate/Input/InputMasked";

interface LoginFormValues {
  email: string;
  password: string;
  code: string | undefined;
}

const validationSchema = yup.object().shape({
  email: yup.string().required('O endereço de email é obrigatório').email('Digite um email válido'),
  password: yup.string().required('A senha é obrigatória'),
  code: yup.string(),
});


const PageLogin = () => {

  const navigate = useNavigate();
  const { login, resendEmailVerification } = useAuth();
  const [email, setEmail] = useState("");

  const { handleSubmit, control, formState } = useForm<LoginFormValues>({
    resolver: yupResolver(validationSchema),
  });
  const { errors } = formState;

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isNeedVerifyEmail, setIsNeedVerifyEmail] = useState(false);

  const handleResendEmail = async () => {
      const response = await resendEmailVerification(email);
      console.log(response)
      if (response && response.status == "success") {
        showToastSuccess(response.message ?? "E-mail reenviado com sucesso!");
      } else {
        showToastError("Ocorreu um erro ao reenviar o e-mail de verificação.");
      }
  }

  const onSubmit = async (data: LoginFormValues) => {
    setLoading(true);
    try {
      const response = await login(data.email, data.password, data.code);

      setEmail(data.email);
      if (!response.status && response.require_email) {
        setIsNeedVerifyEmail(true);
      }

      if (response.status) {
        showToastSuccess("Login realizado com sucesso!");
        navigate('/');
        window.location.reload();
      }

      setLoading(false);

    } catch (error) {
      showToastError("Essas credenciais não correspondem aos nossos registros.");
      setLoading(false);
    }
  };

  return (
    <Layout>
      <header className="text-center max-w-2xl mx-auto mb-14 sm:mb-16 ">
        <Heading2>Login</Heading2>
        <span className="block text-sm mt-2 text-neutral-700 sm:text-base dark:text-neutral-200">
          Bem-vinda a Radar & Você!
        </span>
      </header>

      <div className="max-w-md mx-auto space-y-6">
        {/* FORM */}
        <form className="grid grid-cols-1 gap-6" onSubmit={handleSubmit(onSubmit)}>
          <label className="block">
            <span className="text-neutral-800 dark:text-neutral-200">Endereço de Email</span>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <>
                  <Input {...field} autoComplete="username" value={field.value || ''}/>
                  {errors.email && <small className="text-red-500">{errors.email.message}</small>}
                </>
              )}
            />
          </label>
          <label className="block">
            <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200">
              Senha
              <NcLink href="/forgot-pass" className="text-sm">
                Esqueceu a senha?
              </NcLink>
            </span>
            
            <div className="relative">
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <>
                    <Input {...field} type={showPassword ? "text" : "password"} autoComplete="current-password" value={field.value || ''}/>
                    {errors.password && <small className="text-red-500">{errors.password.message}</small>}
                  </>
                )}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 focus:outline-none"
              >
                {showPassword ? (
                  <AiOutlineEyeInvisible color="gray" size={22} />
                ) : (
                  <AiOutlineEye color="gray" size={22} />
                )}
              </button>
            </div>
          </label>

          { isNeedVerifyEmail && (
            <>
              <div className=" mt-5 w-100 px-8 py-5 rounded-lg border bg-gray-50 dark:bg-gray-700">
                  <span className="text-primary-6000">Confirmação de e-mail necessária</span>
                  <p className="mt-3 text-justify text-sm dark:text-white">
                    Foi enviado ao e-mail cadastrado um codigo de verificação, insira-o no campo abaixo para prosseguir.
                    Caso o e-mail não tenha sido recebido, aguarde alguns minutos e o envie novamente.
                  </p>
              </div>




              <label className="block">
                  {/* <span className="text-neutral-800 dark:text-neutral-200">Código de confirmação</span> */}
                  <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200">
                    Código de confirmação
                    <span onClick={() => handleResendEmail()} className="text-sm">
                      Reenviar e-mail
                    </span>
                  </span>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <Controller
                        name="code"
                        control={control}
                        render={({ field }) => (
                          <>
                            {/* <Input {...field} className="rounded-r-lg" type="text" autoComplete="document" value={field.value || ''}/> */}

                            <InputMasked
                              {...field}
                              type="text"
                              className="rounded mt-1"
                              autoComplete="code"
                              mask={'999999'}
                            />

                            {errors.code && <small className="text-red-500">{errors.code.message}</small>}
                          </>
                        )}
                      />
                    </div>
                    {/* <ButtonPrimary outline type="button">Não recebi</ButtonPrimary> */}
                  </div>
              </label>
            </>
          )}

          <ButtonPrimary className="mt-5" loading={loading} type="submit">Continuar</ButtonPrimary>
        </form>
        {/* ==== */}
        <span className="block text-center text-neutral-700 dark:text-neutral-300">
          Nova por aqui ? {` `}
          <NcLink href="/cadastro">Crie sua conta</NcLink>
        </span>
      </div>
    </Layout>
  );
};

export default PageLogin;
